<template>
  <div class="chart-type-selector">
    <!-- Header avec titre -->
    <div class="mb-4">
      <h3 class="text-lg font-semibold text-gray-900 mb-1">
        Configuration du graphique
      </h3>
      <p class="text-sm text-gray-600">
        Choisissez d'abord le style de visualisation, puis configurez chaque série
      </p>
    </div>

    <!-- Étape 1: Sélection du système de coordonnées -->
    <div class="mb-6">
      <h4 class="text-sm font-medium text-gray-700 mb-3">1. Style de visualisation</h4>
      <div class="flex gap-4">
        <div v-for="(systemConfig, systemKey) in COORDINATE_SYSTEMS" :key="systemKey"
          @click="selectCoordinateSystem(systemKey as CoordinateSystem)"
          class="flex-1"
          :class="[
            'border rounded-lg cursor-pointer transition-all touch-manipulation',
            'hover:shadow-md active:scale-95',
            selectedCoordinateSystem === systemKey 
              ? 'border-blue-500 bg-blue-50 shadow-sm' 
              : 'border-gray-200 hover:border-gray-300'
          ]">
          
          <div class="p-4 text-center">
            <!-- Icône -->
            <div class="text-3xl mb-2">{{ systemConfig.icon }}</div>
            
            <!-- Titre -->
            <h5 class="font-medium text-sm text-gray-900 mb-1">
              {{ systemConfig.name }}
            </h5>
            
            <!-- Description -->
            <p class="text-xs text-gray-500 line-clamp-2">
              {{ systemConfig.description }}
            </p>

            <!-- Capacités -->
            <div class="mt-2 flex flex-wrap justify-center gap-1">
              <span v-if="systemConfig.supportsMultipleSeries" 
                class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                Multi-séries
              </span>
              <span v-if="systemConfig.supportsTimeAxis" 
                class="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                Temporel
              </span>
            </div>
          </div>

          <!-- Badge de sélection -->
          <div v-if="selectedCoordinateSystem === systemKey" 
            class="absolute top-2 right-2 w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Étape 2: Types disponibles pour le système sélectionné -->
    <div v-if="selectedCoordinateSystem">
      <h4 class="text-sm font-medium text-gray-700 mb-3">
        2. Types disponibles pour {{ COORDINATE_SYSTEMS[selectedCoordinateSystem].name.toLowerCase() }}
      </h4>
      
      <div class="flex overflow-x-auto gap-3 pb-2 snap-x snap-mandatory scrollbar-hidden">
        <div v-for="chartType in availableChartTypes" :key="chartType"
          @click="selectDefaultChartType(chartType)"
          class="flex-shrink-0 w-28 snap-start"
          :class="[
            'border rounded-lg cursor-pointer transition-all touch-manipulation',
            'hover:shadow-md active:scale-95',
            selectedDefaultType === chartType 
              ? 'border-green-500 bg-green-50 shadow-sm' 
              : 'border-gray-200 hover:border-gray-300'
          ]">
          
          <div class="p-3 text-center">
            <!-- Icône -->
            <div class="text-xl mb-1">{{ getChartTypeIcon(chartType) }}</div>
            
            <!-- Titre -->
            <h5 class="font-medium text-xs text-gray-900 mb-1">
              {{ getChartTypeLabel(chartType) }}
            </h5>
            
            <!-- Badge de type par défaut -->
            <div v-if="selectedDefaultType === chartType" 
              class="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
              Par défaut
            </div>
          </div>
        </div>
      </div>

      <!-- Information sur la configuration par série -->
      <div class="mt-3 p-3 bg-amber-50 rounded-lg">
        <div class="flex items-center gap-2 mb-1">
          <span class="text-amber-600">💡</span>
          <span class="text-sm font-medium text-amber-700">
            Configuration avancée
          </span>
        </div>
        <p class="text-xs text-amber-600">
          Le type sélectionné sera utilisé par défaut. Vous pourrez choisir un type spécifique 
          pour chaque série de données dans l'étape suivante.
        </p>
      </div>
    </div>

    <!-- Informations détaillées du système sélectionné -->
    <Transition name="fade" mode="out-in">
      <div v-if="selectedSystemConfig" class="mt-4 p-4 bg-gray-50 rounded-lg">
        <div class="flex items-start gap-3">
          <span class="text-2xl">{{ selectedSystemConfig.icon }}</span>
          <div class="flex-1">
            <h4 class="font-medium text-gray-900 mb-1">
              {{ selectedSystemConfig.name }}
            </h4>
            <p class="text-sm text-gray-600 mb-3">
              {{ selectedSystemConfig.description }}
            </p>
            
            <!-- Caractéristiques techniques -->
            <div class="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span class="text-gray-500">Multi-séries :</span>
                <span class="font-medium ml-1">
                  {{ selectedSystemConfig.supportsMultipleSeries ? '✅' : '❌' }}
                </span>
              </div>
              <div>
                <span class="text-gray-500">Axe temporel :</span>
                <span class="font-medium ml-1">
                  {{ selectedSystemConfig.supportsTimeAxis ? '✅' : '❌' }}
                </span>
              </div>
              <div>
                <span class="text-gray-500">Axes doubles :</span>
                <span class="font-medium ml-1">
                  {{ selectedSystemConfig.supportsSecondaryAxis ? '✅' : '❌' }}
                </span>
              </div>
              <div>
                <span class="text-gray-500">Types :</span>
                <span class="font-medium ml-1">{{ selectedSystemConfig.types.length }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { 
  FLEXIBLE_CHART_TYPE_OPTIONS, 
  COORDINATE_SYSTEMS, 
  getAvailableChartTypes,
  type FlexibleChartType, 
  type CoordinateSystem 
} from '../../types/customCharts'

interface Props {
  // Nouveau système : coordinateSystem comme valeur principale
  modelValue: CoordinateSystem
  // Type par défaut pour les nouvelles séries
  defaultChartType?: FlexibleChartType
  compactMode?: boolean
  showRecommendations?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: CoordinateSystem): void
  (e: 'update:defaultChartType', value: FlexibleChartType): void
  (e: 'change', data: { coordinateSystem: CoordinateSystem, defaultChartType: FlexibleChartType }): void
}

const props = withDefaults(defineProps<Props>(), {
  compactMode: false,
  showRecommendations: true,
  defaultChartType: 'line'
})

const emit = defineEmits<Emits>()

// État local
const selectedCoordinateSystem = ref<CoordinateSystem>(props.modelValue)
const selectedDefaultType = ref<FlexibleChartType>(props.defaultChartType)

// Configuration du système sélectionné
const selectedSystemConfig = computed(() => {
  return selectedCoordinateSystem.value ? COORDINATE_SYSTEMS[selectedCoordinateSystem.value] : null
})

// Types disponibles pour le système sélectionné
const availableChartTypes = computed(() => {
  if (!selectedCoordinateSystem.value) return []
  return getAvailableChartTypes(selectedCoordinateSystem.value)
})

// Méthodes
const selectCoordinateSystem = (system: CoordinateSystem) => {
  selectedCoordinateSystem.value = system
  
  // Auto-sélectionner le premier type disponible comme défaut
  const availableTypes = getAvailableChartTypes(system)
  if (availableTypes.length > 0 && !availableTypes.includes(selectedDefaultType.value)) {
    selectedDefaultType.value = availableTypes[0]
  }
  
  emit('update:modelValue', system)
  emit('update:defaultChartType', selectedDefaultType.value)
  emit('change', { 
    coordinateSystem: system, 
    defaultChartType: selectedDefaultType.value 
  })
}

const selectDefaultChartType = (chartType: FlexibleChartType) => {
  selectedDefaultType.value = chartType
  emit('update:defaultChartType', chartType)
  emit('change', { 
    coordinateSystem: selectedCoordinateSystem.value, 
    defaultChartType: chartType 
  })
}

const getChartTypeIcon = (chartType: FlexibleChartType): string => {
  const option = FLEXIBLE_CHART_TYPE_OPTIONS.find(opt => opt.type === chartType)
  return option?.icon || '📊'
}

const getChartTypeLabel = (chartType: FlexibleChartType): string => {
  const option = FLEXIBLE_CHART_TYPE_OPTIONS.find(opt => opt.type === chartType)
  return option?.label || chartType
}

// Watchers
watch(() => props.modelValue, (newValue) => {
  selectedCoordinateSystem.value = newValue
})

watch(() => props.defaultChartType, (newValue) => {
  if (newValue) {
    selectedDefaultType.value = newValue
  }
})

// S'assurer que le type par défaut est compatible avec le système
watch(selectedCoordinateSystem, (newSystem) => {
  const availableTypes = getAvailableChartTypes(newSystem)
  if (!availableTypes.includes(selectedDefaultType.value)) {
    selectedDefaultType.value = availableTypes[0] || 'line'
    emit('update:defaultChartType', selectedDefaultType.value)
  }
})
</script>

<style scoped>
@reference "../../style.css";

/* Scroll horizontal fluide */
.scrollbar-hidden {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hidden::-webkit-scrollbar {
  display: none;
}

/* Améliorer le snapping sur mobile */
.snap-x {
  scroll-snap-type: x mandatory;
}

.snap-start {
  scroll-snap-align: start;
}

/* Optimisations tactiles */
.touch-manipulation {
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

/* Animations fluides */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Limitation de lignes pour mobile */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* États actifs pour mobile */
@media (hover: none) {
  .hover\:shadow-md:hover {
    box-shadow: none;
  }
  
  .active\:scale-95:active {
    transform: scale(0.95);
    transition: transform 0.1s ease-in-out;
  }
}

/* Adaptations pour petits écrans */
@media (max-width: 640px) {
  .chart-type-selector .w-32 {
    min-width: 128px; /* Assurer une taille minimum */
  }
  
  .grid-cols-2 {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }
}

/* Mode compact */
.compact-mode .text-lg {
  font-size: 1rem;
}

.compact-mode .p-4 {
  padding: 0.75rem;
}

.compact-mode .w-32 {
  width: 7rem;
}
</style>